import { Box, Card, CardContent } from '@mui/material';
import React, { useEffect, useRef } from 'react';
import { Palette } from 'Palette';
import { RADIUS } from 'DesignTokens';
import AddContentBlockButton from './AddContentBlockButton';
import ContentBlockToolbar from './ContentBlockToolbar';
import { ContentBlockWrapperProps } from './newContentManagementEditorTypes';

// MUI's default Card corner radius (4px) reads noticeably sharper than the
// rest of the admin UI's panels (toolbar, Fund Details, etc.), which all use
// the standard 12px section radius — matched here, with overflow hidden so
// the toolbar strip's flat top doesn't poke past the now-rounded corners.
const BLOCK_CARD_SX = {
  borderRadius: RADIUS.section,
  borderColor: Palette.default.border,
  overflow: 'hidden',
} as const;

const FLASH_SX = {
  '@keyframes blockFlash': {
    from: { boxShadow: '0 0 0 3px rgba(25, 118, 210, 0.55)' },
    to: { boxShadow: '0 0 0 3px rgba(25, 118, 210, 0)' },
  },
  animation: 'blockFlash 1.6s ease-out',
  borderRadius: RADIUS.section,
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
} as const;

export default function ContentBlockWrapper({
  index,
  total,
  blockType,
  blockId,
  onMove,
  onRemove,
  onAdd,
  hideInsertSlots = false,
  toolbarAction,
  highlighted = false,
  highlightLead = false,
  children,
}: ContentBlockWrapperProps) {
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (highlighted && highlightLead && rootRef.current) {
      rootRef.current.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  }, [highlighted, highlightLead]);

  return (
    <Box
      id={`editor-block-${blockId}`}
      ref={rootRef}
      data-highlighted={highlighted || undefined}
      sx={{ mb: 2 }}
    >
      {!hideInsertSlots && <AddContentBlockButton index={index} where="before" onAdd={onAdd} />}
      <Card variant="outlined" sx={[BLOCK_CARD_SX, highlighted && FLASH_SX]}>
        <ContentBlockToolbar
          index={index}
          total={total}
          blockType={blockType}
          onMove={onMove}
          onRemove={onRemove}
          action={toolbarAction}
        />
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>{children}</CardContent>
      </Card>
      {!hideInsertSlots && index === total - 1 && (
        <AddContentBlockButton index={index} where="after" onAdd={onAdd} />
      )}
    </Box>
  );
}
